"use client";

import { useRef, useState } from "react";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useIdentite } from "@/hooks/useSolopreneurProfile";
import { nettoyerIdentite } from "@/lib/tools/dossier";
import { libelleChamp } from "@/lib/tools/dossierTextes";

/**
 * L'identité des formulaires, saisie une fois.
 *
 * UNE ÉTAPE À PART, PROPOSÉE AU PREMIER FORMULAIRE. Les six questions de
 * « Votre situation » servent aux calculs ; l'identité ne sert qu'aux
 * documents, et la demander d'entrée à celui qui veut savoir ce qu'il lui
 * reste serait hors de propos. Elle se propose donc là où elle sert, dans un
 * formulaire, et remplit ensuite les douze.
 *
 * ELLE NE QUITTE JAMAIS L'APPAREIL, même quand le dossier suit le compte : un
 * nom, une adresse et une date de naissance n'ont rien à faire sur nos
 * serveurs pour remplir un PDF dans le navigateur. Le numéro de sécurité
 * sociale n'y figure pas : on le tape dans le seul formulaire qui le demande.
 *
 * Tous les champs sont facultatifs : un formulaire garde les siens vides et
 * le lecteur les remplit sur place.
 */

const T = {
    fr: {
        titre: "Votre identité",
        intro: "Remplie une fois, elle complète les douze formulaires des outils. Elle reste sur cet appareil : rien n'est envoyé, même si vous êtes connecté.",
        blocs: { personne: "Vous", naissance: "Naissance", adresse: "Adresse", entreprise: "Votre entreprise" },
        madame: "Madame",
        monsieur: "Monsieur",
        nomNaissanceAide: "Seulement s'il est différent de votre nom.",
        siretAide: "Les 14 chiffres, sur votre avis de situation Sirene.",
        nss: "Votre numéro de sécurité sociale n'est jamais gardé : vous le tapez dans le seul formulaire qui le demande.",
        enregistrer: "Enregistrer mon identité",
        annuler: "Annuler",
        effacer: "Effacer mon identité de cet appareil",
        siretInvalide: "Un SIRET compte 14 chiffres.",
    },
    en: {
        titre: "Your identity",
        intro: "Fill it in once and it completes the twelve forms in the tools. It stays on this device: nothing is sent, even when you are signed in.",
        blocs: { personne: "You", naissance: "Birth", adresse: "Address", entreprise: "Your business" },
        madame: "Ms",
        monsieur: "Mr",
        nomNaissanceAide: "Only if it differs from your last name.",
        siretAide: "The 14 digits, on your Sirene registration notice.",
        nss: "Your social security number is never kept: you type it in the one form that asks for it.",
        enregistrer: "Save my identity",
        annuler: "Cancel",
        effacer: "Erase my identity from this device",
        siretInvalide: "A SIRET has 14 digits.",
    },
};

const BLOCS = [
    ["personne", ["prenoms", "nom", "nomNaissance", "nationalite", "email", "telephone"]],
    ["naissance", ["dateNaissance", "villeNaissance", "paysNaissance"]],
    ["adresse", ["voie", "complement", "codePostal", "commune"]],
    ["entreprise", ["siret", "codeApe"]],
];

const TYPES = { dateNaissance: "date", email: "email", telephone: "tel" };
const AUTOCOMPLETE = {
    prenoms: "given-name",
    nom: "family-name",
    email: "email",
    telephone: "tel",
    voie: "address-line1",
    complement: "address-line2",
    codePostal: "postal-code",
    commune: "address-level2",
    dateNaissance: "bday",
};

const CHAMP =
    "w-full rounded-lg border border-gray-200 dark:border-white/15 bg-white dark:bg-white/[0.04] px-3 py-2.5 text-base dark:text-white focus:border-primary focus:outline-hidden";

function Formulaire({ lang, identite, onFermer, onEnregistre }) {
    const t = T[lang] || T.fr;
    const { enregistrer, effacer } = useIdentite();
    const [valeurs, setValeurs] = useState(() => ({ ...identite }));
    const [erreur, setErreur] = useState(null);
    const champSiret = useRef(null);

    const changer = (champ) => (e) => {
        setValeurs((v) => ({ ...v, [champ]: e.target.value }));
        // L'erreur s'efface dès qu'on corrige le champ fautif.
        if (champ === "siret") setErreur(null);
    };

    const valider = (e) => {
        e.preventDefault();
        const siret = (valeurs.siret || "").replace(/\s/g, "");
        if (siret && !/^\d{14}$/.test(siret)) {
            setErreur(t.siretInvalide);
            champSiret.current?.focus();
            return;
        }
        enregistrer(nettoyerIdentite({ ...valeurs, siret }));
        onEnregistre?.();
        onFermer();
    };

    return (
        <form onSubmit={valider} className="p-6 sm:p-8" noValidate>
            <h2 id="identite-titre" className="text-xl font-bold dark:text-white font-sourceSans mb-2">{t.titre}</h2>
            <p className="text-sm text-gray-600 dark:text-lightBlue mb-6">{t.intro}</p>

            <fieldset className="mb-5">
                <legend className="sr-only">{libelleChamp("civilite", lang)}</legend>
                <div className="grid grid-cols-2 gap-2">
                    {["madame", "monsieur"].map((c) => (
                        <label
                            key={c}
                            className={`flex min-h-[44px] items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold cursor-pointer transition-colors duration-200 focus-within:ring-2 focus-within:ring-primary/50 ${
                                valeurs.civilite === c
                                    ? "border-gray-900 dark:border-white text-gray-900 dark:text-white"
                                    : "border-gray-200 dark:border-white/15 text-gray-600 dark:text-lightBlue"
                            }`}
                        >
                            <input
                                type="radio"
                                name="civilite"
                                value={c}
                                checked={valeurs.civilite === c}
                                onChange={changer("civilite")}
                                className="sr-only"
                            />
                            {t[c]}
                        </label>
                    ))}
                </div>
            </fieldset>

            {BLOCS.map(([bloc, champs]) => (
                <fieldset key={bloc} className="mb-6">
                    <legend className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-lightBlue/70 mb-3">
                        {t.blocs[bloc]}
                    </legend>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {champs.map((champ) => (
                            <label
                                key={champ}
                                className={`flex flex-col gap-1 ${champ === "voie" || champ === "complement" ? "sm:col-span-2" : ""}`}
                            >
                                <span className="text-sm font-semibold dark:text-white">{libelleChamp(champ, lang)}</span>
                                {TYPES[champ] === "date" ? (
                                    // Une date NON CONTRÔLÉE, comme partout dans les outils
                                    // (ChampDate, Journey.jsx) : pilotée par value, les
                                    // segments tapés au clavier sautaient.
                                    <input
                                        type="date"
                                        defaultValue={valeurs[champ] || ""}
                                        onChange={changer(champ)}
                                        autoComplete={AUTOCOMPLETE[champ]}
                                        className={CHAMP}
                                    />
                                ) : (
                                    <input
                                        ref={champ === "siret" ? champSiret : undefined}
                                        type={TYPES[champ] || "text"}
                                        inputMode={champ === "siret" || champ === "codePostal" ? "numeric" : undefined}
                                        autoComplete={AUTOCOMPLETE[champ] || "off"}
                                        value={valeurs[champ] || ""}
                                        onChange={changer(champ)}
                                        aria-invalid={champ === "siret" && erreur ? true : undefined}
                                        aria-describedby={champ === "siret" && erreur ? "identite-siret-erreur" : undefined}
                                        className={CHAMP}
                                    />
                                )}
                                {champ === "nomNaissance" && (
                                    <span className="text-xs text-gray-500 dark:text-lightBlue/70">{t.nomNaissanceAide}</span>
                                )}
                                {champ === "siret" &&
                                    (erreur ? (
                                        <span id="identite-siret-erreur" role="alert" className="text-xs text-red-600 dark:text-red-400">
                                            {erreur}
                                        </span>
                                    ) : (
                                        <span className="text-xs text-gray-500 dark:text-lightBlue/70">{t.siretAide}</span>
                                    ))}
                            </label>
                        ))}
                    </div>
                </fieldset>
            ))}

            <p className="flex items-start gap-2 rounded-lg bg-blue-50 dark:bg-blue-500/10 px-3 py-2.5 text-sm text-blue-900 dark:text-blue-200 mb-5">
                <SolarIcon name="shield-check-linear" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{t.nss}</span>
            </p>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <button
                    type="button"
                    onClick={onFermer}
                    className="min-h-[44px] rounded-lg border border-gray-200 dark:border-white/15 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-lightBlue"
                >
                    {t.annuler}
                </button>
                <button
                    type="submit"
                    className="min-h-[44px] rounded-lg bg-gray-900 dark:bg-white px-4 py-2.5 text-sm font-semibold text-white dark:text-gray-900 transition-opacity hover:opacity-90"
                >
                    {t.enregistrer}
                </button>
            </div>

            {Object.values(identite || {}).some(Boolean) && (
                <button
                    type="button"
                    onClick={() => {
                        effacer();
                        onEnregistre?.();
                        onFermer();
                    }}
                    className="mt-5 inline-flex min-h-[44px] items-center text-sm text-gray-500 dark:text-lightBlue/70 underline underline-offset-2 hover:text-red-600 dark:hover:text-red-400"
                >
                    {t.effacer}
                </button>
            )}
        </form>
    );
}

/** La feuille d'identité. Le formulaire se remonte à chaque ouverture, sur l'identité du moment. */
export default function ToolsIdentitySheet({ lang = "fr", ouvert, onFermer, onEnregistre }) {
    const { identite } = useIdentite();
    return (
        <BottomSheetModal isOpen={ouvert} onClose={onFermer} maxWidthClass="sm:max-w-xl" labelledBy="identite-titre">
            {ouvert && <Formulaire lang={lang} identite={identite} onFermer={onFermer} onEnregistre={onEnregistre} />}
        </BottomSheetModal>
    );
}
